import React from 'react';
import Link from 'next/link';
import nProgress from 'nprogress';
import Image from 'next/image';
import { ShoppingCarIcon, formatCurrency } from '@avila-tek/ui/src';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { Product, ShopCart } from '../../../models';
import { useNotify, useShopCart, useUser } from '../../../hooks';
import { ADD_ITEM_TO_SHOP_CART } from '../../../graphql/mutation';
import { getPosibleOptions } from '../../../lib';
import img from '../../../public/images/meit-purple.png';

interface ProductCardProps {
  product: Product;
  minPrice?: number;
  maxPrice?: number;
}

function ProductCard({ product, minPrice, maxPrice }: ProductCardProps) {
  const [user] = useUser();
  const notify = useNotify();
  const router = useRouter();
  const [shopCart, , refreshShopCart] = useShopCart();

  const [disabled, setDisabled] = React.useState(false);
  const [selectedVariant, setSelectedVariant] = React.useState(
    minPrice && maxPrice
      ? product?.variants?.find(
          (variant) => minPrice <= variant?.price && variant?.price <= maxPrice
        )
      : product?.variants[0]
  );

  const [addItemToShopCartMutation] = useMutation<{
    addItemToShopCart: {
      ShopCart: ShopCart;
    };
  }>(ADD_ITEM_TO_SHOP_CART);

  const addItemToShopCart = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    if (disabled) return;
    if (!user) {
      return notify(
        'Debes iniciar sesión para agregar productos al carrito',
        'error'
      );
    }
    if (!selectedVariant?._id) {
      return notify(
        'Seleccione el producto que desea añadir al carrito',
        'error'
      );
    }
    try {
      nProgress.start();
      setDisabled(true);
      const { data } = await addItemToShopCartMutation({
        variables: {
          data: {
            items: [
              {
                variant: selectedVariant?._id,
                shopList: [
                  {
                    quantity: 1,
                    client: user?.client?._id,
                  },
                ],
              },
            ],
            shopCart: shopCart?._id ?? user?.client?.shopCart?._id,
          },
        },
      });
      if (data?.addItemToShopCart) {
        await refreshShopCart();
        notify('Has agregado el producto al carrito exitosamente', 'success');
      } else {
        notify(
          'Ha ocurrido un error al agregar el producto al carrito',
          'error'
        );
      }
    } catch (error) {
      return notify('Ha ocurrido un error al agregar al carrito', 'error');
    } finally {
      nProgress.done();
      setDisabled(false);
    }
  };

  return (
    <div
      className={`bg-system-100 rounded-lg drop-shadow ease-in-out duration-300 flex flex-col overflow-hidden items-center justify-center pb-2 ${
        minPrice && maxPrice ? '' : 'w-[230px] flex-shrink-0'
      }`}
    >
      <div className="w-full">
        <Link
          href={`/app/catalogue/gift-card/${product?.slug}?variant=${selectedVariant?._id}`}
        >
          {selectedVariant?.photos?.length ? (
            <img
              src={selectedVariant?.photos[0]?.src}
              alt={selectedVariant?.title}
              className="object-cover w-full h-[180px] object-center cursor-pointer"
            />
          ) : (
            <Image
              alt="Imagen de perfil"
              src={img}
              className=" w-full h-[180px] object-center cursor-pointer"
            />
          )}
        </Link>
      </div>

      <div className="w-full flex flex-col items-center h-2/5 px-4 pt-2 justify-between gap-2">
        <div className="w-full flex items-start justify-start">
          <Link
            href={`/app/catalogue/gift-card/${product?.slug}?selectedVariant=${selectedVariant?._id}&category=${router?.query?.categoryId}&ally=${router?.query?.allyQueryParam}`}
          >
            <p className="w-full text-neutral-cold-500 font-bold text-lg text-left hover:underline hover:text-color-primary-text-300 ease-in-out duration-150">
              {product?.title}
            </p>
          </Link>
        </div>
        <div className="w-full flex flex-col gap-2">
          <div className="w-full flex flex-row overflow-auto gap-2 pb-1">
            {getPosibleOptions(product)?.map((option) => (
              <button
                key={option?.variant?._id}
                onClick={() => {
                  setSelectedVariant(option?.variant);
                }}
                onMouseOver={() => {
                  setSelectedVariant(option?.variant);
                }}
                onFocus={() => {
                  setSelectedVariant(option?.variant);
                }}
                type="button"
                className={`h-full flex-shrink-0 w-fit ease-in-out rounded-sm duration-150 border px-2 text-sm text-center flex justify-center items-center text-color-primary-text-300  cursor-pointer ${
                  selectedVariant?._id === option?.variant?._id
                    ? 'bg-color-primary-50 border-color-primary-300'
                    : 'hover:bg-system-300 border-system-400'
                }`}
              >
                {option?.value}
              </button>
            ))}
          </div>
        </div>
        <div className="w-full flex flex-row items-end justify-between gap-2 pb-2">
          <div className="w-full flex mx-auto gap-2 items-center">
            <span className="text-color-primary-text-300 text-xl font-extrabold">
              ${formatCurrency(selectedVariant?.price)}
            </span>
            {selectedVariant?.compareAtPrice && (
              <span className="text-neutral-cold-400 line-through">
                ${formatCurrency(selectedVariant?.compareAtPrice)}
              </span>
            )}
          </div>
          <button
            disabled={disabled}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              addItemToShopCart(e);
            }}
            className="bg-color-primary-300 ease-in-out duration-150 p-2 rounded-md hover:bg-color-primary-100 text-white"
          >
            <ShoppingCarIcon className="w-5 cursor-pointer fill-purple-300" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductCard;
